import { useState } from 'react';
import {
  styled,
  ParagraphText,
  Modal,
  StackLayout,
  HeadingText,
  Divider,
} from '@leagueplatform/genesis-core';
import { useFiltering } from '../../utils/useFiltering';
import type { ParsedAnalyticsSchema } from '../../types/AnalyticsSchema';

export function Table() {
  const { data } = useFiltering();
  const [modalContent, setModatContent] = useState<ParsedAnalyticsSchema>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const Table = styled('table', {
    borderSpacing: '0 8px',
    marginY: '$oneAndHalf',
    width: '100%',
  });

  const TableRow = styled('tr', {
    height: '$twoAndHalf',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: '$surfaceBackgroundSecondary',
    },
  });

  const TableDataCell = styled('td', {
    paddingInlineEnd: '$half',
    marginTop: '$half',
    marginBottom: '$half',
  });

  const TableHeaderCell = styled('td', {
    typography: '$subtitleOne',
    color: '$onSurfaceTextPrimary',
    minWidth: '200px',
  });

  return (
    <Table>
      <thead>
        <tr>
          <TableHeaderCell>Event Id</TableHeaderCell>
          <TableHeaderCell>Domain</TableHeaderCell>
          <TableHeaderCell>Event Name</TableHeaderCell>
          <TableHeaderCell>Product Area</TableHeaderCell>
          <TableHeaderCell>Screen Name</TableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {data.length === 0 && (
          <TableRow>
            <TableDataCell colSpan={4}>
              <ParagraphText>No data found</ParagraphText>
            </TableDataCell>
          </TableRow>
        )}
        {data.length > 0 &&
          data.map((event: ParsedAnalyticsSchema) => {
            return (
              <TableRow
                key={event.id}
                onClick={() => {
                  setModatContent(event);
                  setIsModalOpen(true);
                }}
              >
                <TableDataCell>
                  <ParagraphText>
                    <ParagraphText emphasis="subtle">
                      <code>{event.id}</code>
                    </ParagraphText>
                  </ParagraphText>
                </TableDataCell>
                <TableDataCell css={{ maxWidth: '200px' }}>
                  <ParagraphText emphasis="subtle">
                    {event.metadata.domain as string}
                  </ParagraphText>
                </TableDataCell>
                <TableDataCell css={{ maxWidth: '200px' }}>
                  <ParagraphText emphasis="subtle">
                    {event.event_name}
                  </ParagraphText>
                </TableDataCell>
                <TableDataCell css={{ maxWidth: '200px' }}>
                  <ParagraphText emphasis="subtle">
                    {event.product_area}
                  </ParagraphText>
                </TableDataCell>

                <TableDataCell
                  css={{
                    width: '20%',
                    paddingInlineEnd: '$none',
                  }}
                >
                  <ParagraphText emphasis="subtle">
                    {event.screen_name}
                  </ParagraphText>
                </TableDataCell>
              </TableRow>
            );
          })}
      </tbody>

      <Modal.Root
        open={isModalOpen}
        onOpenChange={() => {
          setIsModalOpen((previous) => !previous);
        }}
      >
        <Modal.Trigger>open</Modal.Trigger>
        <Modal.Content>
          <Modal.Title>
            <HeadingText level="1" size="xl">
              {modalContent?.id} | {modalContent?.domain}
            </HeadingText>
          </Modal.Title>
          <Modal.Description>
            <ParagraphText>
              Details of the {modalContent?.event_name} event in the{' '}
              {modalContent?.domain} domain.
            </ParagraphText>
          </Modal.Description>
          <Divider />
          <StackLayout>
            {modalContent &&
              Object.entries(modalContent).map(([key, value]) => {
                const valueToDisplay =
                  typeof value === 'object' ? JSON.stringify(value) : value;
                return (
                  <>
                    <HeadingText level="3" size="s">
                      {key}
                    </HeadingText>
                    <ParagraphText>{valueToDisplay}</ParagraphText>
                    <Divider />
                  </>
                );
              })}
          </StackLayout>
          <Modal.Close>Close</Modal.Close>
        </Modal.Content>
      </Modal.Root>
    </Table>
  );
}
